import {useEffect, useMemo, useRef, useState} from 'react';
import {LayoutGrid, List, Video} from 'lucide-react';
import {ToggleGroup, ToggleGroupItem} from '@kroll-resolver-crisp/ui-components-react';
import {ClassifyDialog} from '../components/investigations/ClassifyDialog';
import {ChannelStatusCard} from '../components/channels/ChannelStatusCard';
import {VideoStatusCard} from '../components/channels/VideoStatusCard';
import {EntityThumbnailCard} from '../components/channels/EntityThumbnailCard';
import {useDismissedKeys} from '@/components/submissions/useDismissedKeys';
import {ThumbnailSizeControl} from '@/components/shared/ThumbnailSizeControl';
import {
  ResolvedRow,
  ResolvedSection,
  RowActions,
  SubmissionInputSection,
  SubmissionPageShell
} from '@/components/submissions/SubmissionForm';
import {worstStatusBorderClassName, groupSubmissionsByStatus} from '@/lib/submissionStatusStyle';
import {
  buildChannelItemMetadataFields,
  buildVideoItemMetadataFields
} from '@/lib/videoItemMetadata';
import {
  useUiStore,
  type ThumbnailSize,
  THUMBNAIL_SIZE_MIN,
  THUMBNAIL_SIZE_MAX
} from '@/store/uiStore';
import {cn} from '@/lib/utils';
import {
  CreateP0AlertDialog,
  useP0AlertDialog,
  type P0LinkedItem
} from '@/components/p0alerts/CreateP0AlertDialog';
import {type Item} from '@/api/items';
import {type ChannelStatus} from '@/api/channels';
import {type VideoStatus} from '@/api/videos';
import {type Submission} from '@/api/submissions';
import {usePendingFlags} from '@/components/submissions/usePendingFlags';
import {getApiErrorMessage} from '@/api/client';
import {
  useClassificationConfig,
  useLanguages,
  useTopics,
  useLocations,
  useCheckChannelStatuses,
  useChannelByHandle,
  useVideoStatuses,
  useExistingSubmissions
} from '@/api/hooks';
import {classifyChannelLines, type YouTubeInputKind} from '@/lib/youtube';

// Level 10 is always a single column, at every breakpoint — "as big as possible". Exported so
// other thumbnail-grid pages share the exact same size steps.
export const GRID_COLS_BY_SIZE: Record<ThumbnailSize, string> = {
  1: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9',
  2: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8',
  3: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7',
  4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
  5: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
  6: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4',
  7: 'grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3',
  8: 'grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2',
  9: 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2',
  10: 'grid-cols-1'
};

export type ResolvedEntity = {
  key: string;
  raw: string;
  parsed: YouTubeInputKind;
  loading: boolean;
  error: string | null;
  video: VideoStatus | null;
  channel: ChannelStatus | null;
  submitted: boolean;
  existingSubmissions: Submission[];
};

export function SingleVideoSubmissionPage() {
  // Config data for ClassifyDialog
  const {data: classifications = []} = useClassificationConfig();
  const {data: languages = []} = useLanguages();
  const {data: topics = []} = useTopics();
  const {data: locations = []} = useLocations();

  const [classifyItems, setClassifyItems] = useState<Item[]>([]);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const thumbnailSize = useUiStore((s) => s.thumbnailSize);
  const setThumbnailSize = useUiStore((s) => s.setThumbnailSize);
  const p0Dialog = useP0AlertDialog();
  const dismissed = useDismissedKeys();

  // One or many YouTube video/channel URLs, IDs, or @handles — one per line. Each resolves
  // instantly (debounced) to its own video/channel status card, no submission needed to check.
  const [bulkInput, setBulkInput] = useState('');
  const [entities, setEntities] = useState<ResolvedEntity[]>([]);
  const entitiesRef = useRef<ResolvedEntity[]>([]);
  const checkChannelStatuses = useCheckChannelStatuses();
  const channelByHandle = useChannelByHandle();
  const videoStatuses = useVideoStatuses();
  const existingSubmissionsCheck = useExistingSubmissions();

  useEffect(() => {
    entitiesRef.current = entities;
  }, [entities]);

  const updateEntity = (key: string, patch: Partial<ResolvedEntity>) => {
    setEntities((prev) => prev.map((e) => (e.key === key ? {...e, ...patch} : e)));
  };

  // Best-effort: a failed duplicate-check should never block showing the video/channel status
  // the analyst actually asked for, so it's fetched separately and swallows its own errors.
  const checkExisting = async (key: string, externalId: string) => {
    try {
      const existingSubmissions = await existingSubmissionsCheck.mutateAsync({
        platform: 'youtube',
        externalId
      });
      updateEntity(key, {existingSubmissions});
    } catch {
      // Silently skip — absence of the badge just means "unknown", not "not submitted".
    }
  };

  // Handles @handle lines only, one request each (no batch endpoint exists for handle resolution —
  // YouTube's forHandle= can't take multiple handles per call). Video and channel-id lines go
  // through resolveVideosBatch/resolveChannelsBatch instead, so N ids on the page cost one request
  // rather than N.
  const resolveEntity = async (key: string, parsed: YouTubeInputKind) => {
    try {
      if (parsed.kind === 'handle') {
        const channel = await channelByHandle.mutateAsync(parsed.handle);
        updateEntity(key, {channel, loading: false, error: channel ? null : 'Channel not found'});
        if (channel) {
          void checkExisting(key, channel.id);
        }
      }
    } catch (error) {
      updateEntity(key, {
        loading: false,
        error: getApiErrorMessage(error, 'Failed to look up status')
      });
    }
  };

  const resolveChannelsBatch = async (entries: ReadonlyArray<{key: string; id: string}>) => {
    try {
      const res = await checkChannelStatuses.mutateAsync(entries.map((e) => e.id));
      const byId = new Map(res.results.map((c) => [c.id, c]));
      for (const {key, id} of entries) {
        const channel: ChannelStatus | null = byId.get(id) ?? null;
        updateEntity(key, {channel, loading: false, error: channel ? null : 'Channel not found'});
        if (channel) {
          void checkExisting(key, channel.id);
        }
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to look up status');
      for (const {key} of entries) {
        updateEntity(key, {loading: false, error: message});
      }
    }
  };

  const resolveVideosBatch = async (entries: ReadonlyArray<{key: string; id: string}>) => {
    try {
      const res = await videoStatuses.mutateAsync(entries.map((e) => e.id));
      const byId = new Map(res.results.map((v) => [v.id, v]));
      for (const {key, id} of entries) {
        const video: VideoStatus | null = byId.get(id) ?? null;
        updateEntity(key, {video, loading: false, error: video ? null : 'Video not found'});
        if (video) {
          void checkExisting(key, video.id);
        }
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to look up status');
      for (const {key} of entries) {
        updateEntity(key, {loading: false, error: message});
      }
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const lines = classifyChannelLines(bulkInput);
      const prevByKey = new Map(entitiesRef.current.map((e) => [e.key, e]));
      const next: ResolvedEntity[] = [];
      const toResolve: Array<{key: string; parsed: YouTubeInputKind}> = [];

      for (const line of lines) {
        const key =
          line.parsed.kind === 'unknown' ? `unknown:${line.raw}` : JSON.stringify(line.parsed);
        const existing = prevByKey.get(key);
        if (existing) {
          next.push(existing);
          continue;
        }

        if (line.parsed.kind === 'unknown') {
          next.push({
            key,
            raw: line.raw,
            parsed: line.parsed,
            loading: false,
            error: 'Not a recognizable video/channel URL, ID, or @handle',
            video: null,
            channel: null,
            submitted: false,
            existingSubmissions: []
          });
          continue;
        }

        next.push({
          key,
          raw: line.raw,
          parsed: line.parsed,
          loading: true,
          error: null,
          video: null,
          channel: null,
          submitted: false,
          existingSubmissions: []
        });
        toResolve.push({key, parsed: line.parsed});
      }

      setEntities(next);
      const channelEntries: Array<{key: string; id: string}> = [];
      const videoEntries: Array<{key: string; id: string}> = [];
      for (const {key, parsed} of toResolve) {
        if (parsed.kind === 'channel') {
          channelEntries.push({key, id: parsed.id});
          continue;
        }

        if (parsed.kind === 'video') {
          videoEntries.push({key, id: parsed.id});
          continue;
        }

        void resolveEntity(key, parsed);
      }

      if (channelEntries.length > 0) {
        void resolveChannelsBatch(channelEntries);
      }

      if (videoEntries.length > 0) {
        void resolveVideosBatch(videoEntries);
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkInput]);

  const buildItemForEntity = (entity: ResolvedEntity): Item | null => {
    if (entity.video) {
      const v = entity.video;
      return {
        id: crypto.randomUUID(),
        external_id: v.id,
        platform: 'youtube',
        content_type: 'video',
        title: v.title ?? `YouTube Video (${v.id})`,
        url: `https://www.youtube.com/watch?v=${v.id}`,
        thumbnail_url: v.thumbnail_url,
        author_handle: null,
        // From the same channels.list call already made for monetization. Without it the item is
        // stored with no author, which costs the Apollo message its channel element entirely (the
        // channel is resolved from the item's author id) and leaves nothing linking the video to its
        // channel anywhere else either.
        author_id: v.channel_id,
        author_name: v.channel_title,
        published_at: v.published_at,
        // Same `Fields[].FieldName` shape as Scrapy-sourced items, so ClassifyDialog's language/
        // location auto-select (itemMetadata.ts) and the Apollo message's synthesized channel element
        // both read this fetched-live path identically to a scraped one. Every field here comes from
        // calls the page already makes for monetization (YouTubeChannelClient.GetVideoStatusesAsync) -
        // no extra fetch, no extra quota.
        metadata: {Fields: buildVideoItemMetadataFields(v)},
        affiliate: 'PIONEER',
        created_at: new Date().toISOString(),
        submission_status: null,
        submission_policy_violations: [],
        // Reshaped from the same existingSubmissions probe the badge below already renders, so
        // ClassifyDialog's same-vertical resubmit detection works for this fetched-live entity too
        // (see groupSubmissionsByStatus's own comment).
        submission_status_groups: groupSubmissionsByStatus(entity.existingSubmissions),
        item_status: null,
        is_new_item: null
      };
    }

    if (entity.channel) {
      const c = entity.channel;
      // A content_type of 'actor' plus a real author_id routes this through ClassifyDialog's Actor
      // path server-side (deduplicated by channel id, not treated as a piece of content).
      return {
        id: crypto.randomUUID(),
        external_id: c.id,
        platform: 'youtube',
        content_type: 'actor',
        title: c.title ?? `YouTube Channel (${c.id})`,
        url: c.custom_url
          ? `https://www.youtube.com/${c.custom_url}`
          : `https://www.youtube.com/channel/${c.id}`,
        thumbnail_url: c.thumbnail_url,
        author_handle: c.custom_url,
        author_id: c.id,
        author_name: c.title,
        published_at: c.published_at,
        metadata: {Fields: buildChannelItemMetadataFields(c)},
        affiliate: 'PIONEER',
        created_at: new Date().toISOString(),
        submission_status: null,
        submission_policy_violations: [],
        // Reshaped from the same existingSubmissions probe the badge below already renders, so
        // ClassifyDialog's same-vertical resubmit detection works for this fetched-live entity too
        // (see groupSubmissionsByStatus's own comment).
        submission_status_groups: groupSubmissionsByStatus(entity.existingSubmissions),
        item_status: null,
        is_new_item: null
      };
    }

    return null;
  };

  // An entity flagged earlier is already a real item; classifying it must land on that same one.
  const pendingFlags = usePendingFlags((key) => {
    const entity = entitiesRef.current.find((e) => e.key === key);
    return entity ? buildItemForEntity(entity) : null;
  });

  const openClassify = (toClassify: ResolvedEntity[]) => {
    const rows = toClassify.flatMap((entity) => {
      const item = buildItemForEntity(entity);
      return item ? [{...item, id: pendingFlags.itemIdOf(entity.key) ?? item.id}] : [];
    });
    if (rows.length > 0) {
      setClassifyItems(rows);
    }
  };

  const handleClassifyOne = (entity: ResolvedEntity) => {
    openClassify([entity]);
  };

  // Dedup by resolved channel/video id — an analyst pasting both "@handle" and the channel's raw
  // id (or the same id/handle twice) resolves to two lines that are actually the same entity.
  const visibleEntities = useMemo(() => {
    const seen = new Set<string>();
    const result: ResolvedEntity[] = [];
    for (const entity of entities) {
      const resolvedId = entity.video?.id ?? entity.channel?.id ?? null;
      if (resolvedId) {
        if (seen.has(resolvedId)) {
          continue;
        }

        seen.add(resolvedId);
      }

      result.push(entity);
    }

    return result;
  }, [entities]);

  const classifiableEntities = visibleEntities.filter(
    (e) => !e.submitted && !dismissed.isDismissed(e.key) && (e.video ?? e.channel)
  );

  const classifiableKeys = classifiableEntities.map((e) => e.key);

  const handleClassifyAll = () => {
    openClassify(classifiableEntities);
  };

  const handleSubmitted = (
    succeededItems: Item[],
    linkedItems: P0LinkedItem[],
    wantP0: boolean
  ) => {
    const ids = new Set(succeededItems.map((i) => i.external_id));
    setEntities((prev) =>
      prev.map((e) => {
        const id = e.video?.id ?? e.channel?.id;
        return id && ids.has(id) ? {...e, submitted: true} : e;
      })
    );
    if (wantP0) {
      p0Dialog.launch(linkedItems);
    }
  };

  const resolvedCount = visibleEntities.filter((e) => e.video ?? e.channel).length;
  const unrecognisedCount = visibleEntities.filter((e) => e.parsed.kind === 'unknown').length;

  const renderActions = (entity: ResolvedEntity) => (
    <RowActions
      submitted={entity.submitted}
      flag={pendingFlags.flagOf(entity.key)}
      note={pendingFlags.noteOf(entity.key)}
      subject={entity.video?.title ?? entity.channel?.title ?? entity.raw}
      dismissed={dismissed.isDismissed(entity.key)}
      onFlag={(color, note) => {
        pendingFlags.setFlag(entity.key, color, note);
      }}
      onNote={(note) => {
        pendingFlags.setNote(entity.key, note);
      }}
      onClassify={() => {
        handleClassifyOne(entity);
      }}
      onDismiss={() => {
        dismissed.dismiss(entity.key);
      }}
      onRestore={() => {
        dismissed.restore(entity.key);
      }}
    />
  );

  const duplicateCount = entities.length - visibleEntities.length;
  const lineCount = `${visibleEntities.length} ${visibleEntities.length === 1 ? 'line' : 'lines'}`;
  const duplicateNote =
    duplicateCount > 0
      ? ` · ${duplicateCount} duplicate${duplicateCount === 1 ? '' : 's'} merged`
      : '';

  const viewControls = (
    <>
      {view === 'grid' ? (
        <ThumbnailSizeControl
          min={THUMBNAIL_SIZE_MIN}
          max={THUMBNAIL_SIZE_MAX}
          value={thumbnailSize}
          onChange={(level) => {
            setThumbnailSize(level as ThumbnailSize);
          }}
        />
      ) : null}

      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        aria-label="View"
        value={[view]}
        onValueChange={(next) => {
          if (next[0] === 'list' || next[0] === 'grid') {
            setView(next[0]);
          }
        }}>
        <ToggleGroupItem value="list" aria-label="List view">
          <List />
        </ToggleGroupItem>
        <ToggleGroupItem value="grid" aria-label="Grid view">
          <LayoutGrid />
        </ToggleGroupItem>
      </ToggleGroup>
    </>
  );

  return (
    <SubmissionPageShell>
      <SubmissionInputSection
        title="Submit videos or channels"
        description="Paste one or many YouTube videos or channels, one per line. Each is auto-detected and resolved to its live status; classify them individually or all at once."
        platformLabel="YouTube"
        platformIcon={<Video />}
        inputLabel="Video or channel URLs, IDs or @handles"
        placeholder={
          'Examples:\nhttps://www.youtube.com/watch?v=dQw4w9WgXcQ\nUCwWGT4gzSh7JpRegV3aWIOw\n@bigsmashpadel'
        }
        idleHint="One per line - URLs, IDs and @handles are detected automatically."
        value={bulkInput}
        hasLines={entities.length > 0}
        resolvedCount={resolvedCount}
        unrecognisedCount={unrecognisedCount}
        pendingFlags={pendingFlags}
        classifiableKeys={classifiableKeys}
        flagAllSubject="every item"
        onValueChange={setBulkInput}
        onClassifyAll={handleClassifyAll}
      />

      {entities.length > 0 ? (
        <ResolvedSection
          title="Resolved entities"
          summary={`${lineCount}${duplicateNote}`}
          actions={viewControls}>
          {view === 'grid' ? (
            <div className={cn('grid gap-4', GRID_COLS_BY_SIZE[thumbnailSize])}>
              {visibleEntities.map((entity) => (
                <EntityThumbnailCard
                  key={entity.key}
                  entity={entity}
                  flag={pendingFlags.flagOf(entity.key)}
                  note={pendingFlags.noteOf(entity.key)}
                  dismissed={dismissed.isDismissed(entity.key)}
                  onFlag={(color, note) => {
                    pendingFlags.setFlag(entity.key, color, note);
                  }}
                  onNote={(note) => {
                    pendingFlags.setNote(entity.key, note);
                  }}
                  onClassify={() => {
                    handleClassifyOne(entity);
                  }}
                  onDismiss={() => {
                    dismissed.dismiss(entity.key);
                  }}
                  onRestore={() => {
                    dismissed.restore(entity.key);
                  }}
                />
              ))}
            </div>
          ) : (
            visibleEntities.map((entity) => {
              const borderClassName = worstStatusBorderClassName(
                entity.existingSubmissions.map((s) => s.action_status)
              );
              return (
                <ResolvedRow
                  key={entity.key}
                  raw={entity.raw}
                  loading={entity.loading}
                  error={entity.error}
                  existingSubmissions={entity.existingSubmissions}
                  dismissed={dismissed.isDismissed(entity.key)}>
                  {entity.video ? (
                    <VideoStatusCard
                      video={entity.video}
                      borderClassName={borderClassName}
                      priorSubmissionCount={entity.existingSubmissions.length}
                      footer={renderActions(entity)}
                    />
                  ) : null}
                  {entity.channel ? (
                    <ChannelStatusCard
                      channel={entity.channel}
                      borderClassName={borderClassName}
                      priorSubmissionCount={entity.existingSubmissions.length}
                      footer={renderActions(entity)}
                    />
                  ) : null}
                </ResolvedRow>
              );
            })
          )}
        </ResolvedSection>
      ) : null}

      {/* Classify Dialog */}
      <ClassifyDialog
        items={classifyItems}
        classifications={classifications}
        languages={languages}
        topics={topics}
        locations={locations}
        origin="single_submission"
        onClose={() => {
          setClassifyItems([]);
        }}
        onStartNewBatch={() => {
          setBulkInput('');
          setEntities([]);
          dismissed.clear();
        }}
        onSubmitted={handleSubmitted}
      />

      <CreateP0AlertDialog {...p0Dialog.dialogProps()} />
    </SubmissionPageShell>
  );
}
